import React from 'react';

/** Presentation-only workforce view for the regional forecasting workspace. */
const RegionalWorkforceStage = ({ vm }) => {
  const { ADJUSTMENT_YEARS, Briefcase, ForecastHireRows, ForecastHrRoster, MARKETS, NumericInput, OFFICER_TYPES, RefreshCw, User, WORKFORCE_YEAR_DEFS, activeWorkspaceStage, canEditSubmission, formatLeverValue, formatMillions, formatPercent, hireRows, hrPlan, hrRoster, hrTransfers, lineage, model, outgoingTransferCount, resetWorkforceInputs, setWorkforceTab, submissionId, updateHireRows, updateHrPlan, updateMarketInput, updateWorkforceInput, updateWorkforceYear, workforceInputs, workforceTab, workforceYearInputs } = vm;

  return (
    <>
{activeWorkspaceStage === 'workforce' && <section className="forecast-panel workspace-stage-panel" id="regional-stage-workforce" tabIndex="-1">
        <details className="forecast-accordion" open>
          <summary>
            <div>
              <span className="forecast-section-code">OP_04 · Producer hiring input</span>
              <h2>Producer hiring, attrition, and net financial impact</h2>
            </div>
            <Briefcase size={18} />
          </summary>

          <div className="forecast-accordion-body">

        <div className="workspace-stage-tabs" role="tablist" aria-label="Workforce planning views">
          <button
            type="button"
            className="forecast-secondary-button compact"
            role="tab"
            aria-selected={workforceTab === 'existing'}
            aria-controls="workforce-panel-existing"
            id="workforce-tab-existing"
            tabIndex={workforceTab === 'existing' ? 0 : -1}
            onClick={() => setWorkforceTab('existing')}
          >
            Existing Workforce
          </button>
          <button
            type="button"
            className="forecast-secondary-button compact"
            role="tab"
            aria-selected={workforceTab === 'new-hires'}
            aria-controls="workforce-panel-new-hires"
            id="workforce-tab-new-hires"
            tabIndex={workforceTab === 'new-hires' ? 0 : -1}
            onClick={() => setWorkforceTab('new-hires')}
          >
            New Hires
          </button>
        </div>

        {workforceTab === 'existing' && (
          <div role="tabpanel" id="workforce-panel-existing" aria-labelledby="workforce-tab-existing">
            <div className="forecast-narrative-grid forecast-producer-summary">
              <div><span>Current headcount</span><strong>{hrRoster?.headcount ?? '—'}</strong><small>Latest roster for this planning entity.</small></div>
              <div><span>Expected departures</span><strong>{model.departures}</strong><small>Current attrition assumptions across officer types.</small></div>
              <div><span>Proposed transfers</span><strong>{outgoingTransferCount}</strong><small>Moves requiring receiving-market acceptance.</small></div>
            </div>
            <div className="forecast-subpanel">
              <div className="forecast-panel-head">
                <div>
                  <span className="forecast-section-code">Existing workforce assumptions</span>
                  <h3>Vacancy, merit, attrition, staffing, and roster treatment</h3>
                </div>
                <button type="button" className="forecast-secondary-button compact" disabled={!canEditSubmission} onClick={resetWorkforceInputs}>
                  <RefreshCw size={14} aria-hidden="true" /> Reset assumptions
                </button>
              </div>
              <div className="forecast-segmented" role="group" aria-label="Existing staff method">
                <button type="button" aria-pressed={hrPlan.mode === 'vacancy'} className={hrPlan.mode === 'vacancy' ? 'active' : ''} onClick={() => updateHrPlan({ ...hrPlan, mode: 'vacancy' }, 'mode: vacancy factor')}>Vacancy factor</button>
                <button type="button" aria-pressed={hrPlan.mode === 'roster'} className={hrPlan.mode === 'roster' ? 'active' : ''} onClick={() => updateHrPlan({ ...hrPlan, mode: 'roster' }, 'mode: detailed roster')}>Detailed roster</button>
              </div>
              <div className="forecast-grid forecast-grid-3">
                {Object.entries(workforceInputs).map(([leverId, lever]) => (
                  <label key={leverId} className={lever.value !== lever.baseline ? 'forecast-lever-active' : ''}>
                    <span><strong>{lever.label}</strong><small>Baseline {formatLeverValue(lever, lever.baseline)}</small></span>
                    <NumericInput
                      ariaLabel={lever.label}
                      min={lever.min} max={lever.max} step={lever.step} value={lever.value}
                      disabled={!canEditSubmission}
                      onCommit={(value) => updateWorkforceInput(leverId, value)}
                    />
                  </label>
                ))}
              </div>
              <details className="workspace-change-drawer">
                <summary>Annual workforce assumptions <span>{WORKFORCE_YEAR_DEFS.length}</span></summary>
                <div className="workspace-change-content forecast-home-table-wrap" role="region" aria-label="Annual workforce assumptions" tabIndex="0">
                  <table className="forecast-home-table op3-year-table">
                    <thead><tr><th>Assumption</th><th>Base</th>{ADJUSTMENT_YEARS.map((year) => <th key={year}>FY{year}</th>)}</tr></thead>
                    <tbody>
                      {WORKFORCE_YEAR_DEFS.map((def) => (
                        <tr key={def.id}>
                          <td><strong>{def.label}</strong></td><td className="forecast-mono">{def.base.toFixed(1)}%</td>
                          {ADJUSTMENT_YEARS.map((year, yearIndex) => <td key={year}><NumericInput ariaLabel={`${def.label} FY${year}`} step={def.step} min={def.min} max={def.max} value={workforceYearInputs[def.id][yearIndex]} disabled={!canEditSubmission} onCommit={(value) => updateWorkforceYear(def, yearIndex, value)} /></td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
            {hrPlan.mode === 'roster' && (
              hrRoster?.roster?.length ? (
                <ForecastHrRoster
                  roster={hrRoster.roster}
                  summary={hrRoster}
                  plan={hrPlan}
                  onPlanChange={updateHrPlan}
                  markets={MARKETS.map((market) => market.market)}
                  disabled={Boolean(submissionId) && !canEditSubmission}
                />
              ) : (
                <p className="forecast-submit-note">Loading the employee roster for this unit…</p>
              )
            )}
          </div>
        )}

        {workforceTab === 'new-hires' && <div role="tabpanel" id="workforce-panel-new-hires" aria-labelledby="workforce-tab-new-hires">

        {(hrTransfers?.incoming?.length > 0 || outgoingTransferCount > 0) && (
          <div className="forecast-subpanel transfer-flags">
            <div className="forecast-panel-head">
              <span className="forecast-section-code">OP_04T · Proposed staff transfers</span>
              <h3>Cross-market moves flagged from other packages in this cycle</h3>
            </div>
            <div className="forecast-submit-chips">
              {outgoingTransferCount > 0 && (
                <span className="forecast-health-chip yellow">
                  {outgoingTransferCount} outgoing transfer(s) proposed in your roster plan below
                </span>
              )}
              {(hrTransfers?.incoming || []).map((transfer) => (
                <span className="forecast-health-chip gold" key={transfer.employeeId}>
                  Incoming: {transfer.name || transfer.employeeId} ({transfer.title || 'staff'}
                  {transfer.baseSalary ? ` · $${Math.round(transfer.baseSalary / 1000)}k` : ''}) from{' '}
                  {transfer.fromMarket}, FY{2027 + Math.floor(((transfer.month || 1) - 1) / 12)} M
                  {String((((transfer.month || 1) - 1) % 12) + 1).padStart(2, '0')} — add a hire row to accept
                </span>
              ))}
            </div>
            <p className="forecast-submit-note">
              Incoming transfers are proposals from the sending market's package — they reduce that
              unit's comp at the effective month and land here only when you add the matching hire
              row (use the role's add button below; name it after the transferring employee).
            </p>
          </div>
        )}

        <ForecastHireRows
          hireRows={hireRows}
          officerTypes={OFFICER_TYPES}
          onChange={updateHireRows}
          disabled={Boolean(submissionId) && !canEditSubmission}
        />

        <div className="forecast-narrative-grid forecast-producer-summary">
          <div>
            <span>Gross hires</span>
            <strong>{model.grossHires}</strong>
            <small>{model.departures} expected departures loaded against the market plan.</small>
          </div>
          <div>
            <span>Net hires</span>
            <strong>{model.netHires}</strong>
            <small>Vacancy and attrition shape productive FTE before ramp economics.</small>
          </div>
          <div>
            <span>12M funded loans</span>
            <strong>{formatMillions(model.cappedLoans12)}</strong>
            <small>After line utilization and loan growth appetite cap.</small>
          </div>
          <div>
            <span>12M net impact</span>
            <strong>
              {formatMillions(
                model.newHireLoanInterestIncome +
                  model.depositFundingBenefit +
                  model.fee12 +
                  model.unusedCommitmentFees -
                  model.expense12
              )}
            </strong>
            <small>Pre-provision producer economics from true new production.</small>
          </div>
        </div>

        <div className="forecast-table-shell ace-scroll-shell">
          <table className="forecast-table">
            <thead>
              <tr>
                <th>Officer type</th>
                <th>Gross hires</th>
                <th>Departures</th>
                <th>Vacancy</th>
                <th>Attrition</th>
                <th>Net productive FTE</th>
                <th>12M funded loans</th>
                <th>Line commitments</th>
                <th>12M deposits</th>
                <th>Fee gen.</th>
                <th>Net financial impact</th>
                <th>Modified by</th>
              </tr>
            </thead>
            <tbody>
              {model.officerRows.map((row) => {
                const lineageKey = (field) => lineage[`hire:${row.id}:${field}`];
                const latestUser =
                  lineageKey('grossHires')?.user ||
                  lineageKey('expectedDepartures')?.user ||
                  lineageKey('vacancyOverride')?.user ||
                  lineageKey('attritionOverride')?.user ||
                  'Central baseline';

                return (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.type}</strong>
                      <small>
                        {row.rampMonths} month ramp / {formatPercent(row.lineMix, 0)} line mix / {formatMillions(row.annualExpense)} expense
                      </small>
                    </td>
                    <td>
                      <span className="forecast-static-field compact" title="Driven by hire cohort rows above">
                        {row.grossHires}
                      </span>
                    </td>
                    <td>
                      <NumericInput
                        className=""
                        min={0}
                        value={row.expectedDepartures}
                        onCommit={(value) => updateMarketInput(row.id, 'expectedDepartures', value)}
                      />
                    </td>
                    <td>
                      <NumericInput
                        className=""
                        min={0}
                        step={0.1}
                        value={row.vacancyOverride}
                        onCommit={(value) => updateMarketInput(row.id, 'vacancyOverride', value)}
                      />
                    </td>
                    <td>
                      <NumericInput
                        className=""
                        min={0}
                        step={0.1}
                        value={row.attritionOverride}
                        onCommit={(value) => updateMarketInput(row.id, 'attritionOverride', value)}
                      />
                    </td>
                    <td>{row.vacancyAdjustedFte.toFixed(1)}</td>
                    <td>{formatMillions(row.loans12)}</td>
                    <td>{formatMillions(row.lineCommitments12)}</td>
                    <td>{formatMillions(row.deposits12)}</td>
                    <td>{formatMillions(row.fee12, 2)}</td>
                    <td>
                      {formatMillions(
                        row.loanInterestIncome +
                          row.depositFundingBenefit +
                          row.fee12 +
                          row.unusedCommitmentFees -
                          row.expense12 -
                          row.provision12
                      )}
                    </td>
                    <td>
                      <span className="forecast-user-chip">
                        <User size={12} />
                        {latestUser}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>}
          </div>
        </details>
      </section>}
    </>
  );
};

export default RegionalWorkforceStage;
